import React, { useRef, useState, useMemo, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Text, OrbitControls } from "@react-three/drei";
import { motion } from "framer-motion";
import * as THREE from "three";

// A simple 2D grid component for smaller devices.
// This is the "other thing" that will be used instead of the Three.js scene.
const MobileTechStack = ({ technologies }) => (
  <div className="md:hidden mt-8">
    <div className="grid grid-cols-2 gap-4">
      {technologies.map((tech, index) => (
        <motion.div
          key={tech.name}
          className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.1 }}
        >
          <div className="flex items-center mb-2">
            <div
              className="w-4 h-4 rounded-full mr-3"
              style={{ backgroundColor: tech.color }}
            />
            <span className="font-semibold text-gray-900 dark:text-white">
              {tech.name}
            </span>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {tech.level}% Proficiency
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
            <motion.div
              className="h-1.5 rounded-full"
              style={{ backgroundColor: tech.color }}
              initial={{ width: 0 }}
              animate={{ width: `${tech.level}%` }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
            />
          </div>
        </motion.div>
      ))}
    </div>
  </div>
);

// Individual tech item component for the 3D scene
const TechItem = ({ position, tech, index, isHovered, onHover, onUnhover }) => {
  const meshRef = useRef();
  const textRef = useRef();

  useFrame(({ clock }) => {
    if (meshRef.current && textRef.current) {
      // Floating animation
      const floatY = Math.sin(clock.elapsedTime * 2 + index) * 0.1;
      meshRef.current.position.y = position[1] + floatY;
      textRef.current.position.y = position[1] + floatY + 0.3;

      // Rotation
      meshRef.current.rotation.y = clock.elapsedTime * 0.5 + index;

      // Scale on hover
      const targetScale = isHovered ? 1.2 : 1;
      meshRef.current.scale.lerp(
        new THREE.Vector3(targetScale, targetScale, targetScale),
        0.1
      );
      textRef.current.scale.lerp(
        new THREE.Vector3(targetScale, targetScale, targetScale),
        0.1
      );
    }
  });

  return (
    <group>
      {/* Tech Logo Cube */}
      <mesh
        ref={meshRef}
        position={position}
        onPointerOver={() => onHover(tech)}
        onPointerOut={onUnhover}
      >
        <boxGeometry args={[0.8, 0.8, 0.8]} />
        <meshStandardMaterial
          color={tech.color}
          transparent
          opacity={0.8}
          roughness={0.2}
          metalness={0.1}
        />
      </mesh>

      {/* Tech Name Text */}
      <Text
        ref={textRef}
        position={[position[0], position[1] + 0.8, position[2]]}
        fontSize={0.15}
        color="white"
        anchorX="center"
        anchorY="middle"
        font="/fonts/inter-bold.woff"
      >
        {tech.name}
      </Text>
    </group>
  );
};

// Main 3D scene component
const TechStack3DScene = ({ technologies, hoveredTech, setHoveredTech }) => {
  const groupRef = useRef();

  useFrame(({ clock }) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = Math.sin(clock.elapsedTime * 0.2) * 0.3;
    }
  });

  // Arrange technologies in a spiral pattern
  const positions = useMemo(() => {
    const positions = [];
    const radius = 3;
    const height = 2;

    technologies.forEach((_, index) => {
      const angle = (index / technologies.length) * Math.PI * 2;
      const spiralHeight = (index / technologies.length) * height - height / 2;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      positions.push([x, spiralHeight, z]);
    });

    return positions;
  }, [technologies]);

  return (
    <group ref={groupRef}>
      {/* Ambient lighting */}
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 5, 5]} intensity={0.8} />
      <pointLight position={[-5, -5, -5]} intensity={0.3} color="#4ecdc4" />

      {/* Technology items */}
      {technologies.map((tech, index) => (
        <TechItem
          key={tech.name}
          position={positions[index]}
          tech={tech}
          index={index}
          isHovered={hoveredTech?.name === tech.name}
          onHover={setHoveredTech}
          onUnhover={() => setHoveredTech(null)}
        />
      ))}

      {/* Central core */}
      <mesh position={[0, 0, 0]}>
        <sphereGeometry args={[0.5, 32, 32]} />
        <meshStandardMaterial
          color="#10b981"
          transparent
          opacity={0.7}
          roughness={0.1}
          metalness={0.3}
          emissive="#10b981"
          emissiveIntensity={0.2}
        />
      </mesh>
    </group>
  );
};

const TechStack3D = () => {
  const [hoveredTech, setHoveredTech] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isMobile, setIsMobile] = useState(false);

  // Effect to determine if the screen is mobile or desktop
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768); // You can adjust this breakpoint
    };
    handleResize(); // Set initial state
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const technologies = [
    // Frontend
    { name: "React", category: "frontend", color: "#61DAFB", level: 95 },
    { name: "JavaScript", category: "frontend", color: "#F7DF1E", level: 92 },
    { name: "TypeScript", category: "frontend", color: "#3178C6", level: 88 },
    { name: "Vue.js", category: "frontend", color: "#4FC08D", level: 82 },
    { name: "Nuxt.js", category: "frontend", color: "#00DC82", level: 80 },
    { name: "Three.js", category: "frontend", color: "#000000", level: 78 },

    // Backend
    { name: "Python", category: "backend", color: "#3776AB", level: 94 },
    { name: "Node.js", category: "backend", color: "#339933", level: 85 },
    { name: "FastAPI", category: "backend", color: "#009688", level: 90 },
    { name: "Django", category: "backend", color: "#092E20", level: 87 },

    // Database
    { name: "PostgreSQL", category: "database", color: "#336791", level: 88 },
    { name: "MongoDB", category: "database", color: "#47A248", level: 82 },
    { name: "Redis", category: "database", color: "#DC382D", level: 80 },

    // Tools
    { name: "Docker", category: "tools", color: "#2496ED", level: 82 },
    { name: "AWS", category: "tools", color: "#FF9900", level: 78 },
    { name: "Git", category: "tools", color: "#F05032", level: 93 },
    { name: "CF Workers", category: "tools", color: "#F38020", level: 85 },
    { name: "CF Pages", category: "tools", color: "#F38020", level: 85 },
    { name: "CF D1", category: "tools", color: "#F38020", level: 80 },

    // Design
    { name: "Figma", category: "design", color: "#F24E1E", level: 90 },
    { name: "Photoshop", category: "design", color: "#31A8FF", level: 88 },
  ];

  const categories = [
    { key: "all", label: "All Technologies", color: "#10b981" },
    { key: "frontend", label: "Frontend", color: "#3b82f6" },
    { key: "backend", label: "Backend", color: "#8b5cf6" },
    { key: "database", label: "Database", color: "#f59e0b" },
    { key: "tools", label: "Tools", color: "#ef4444" },
    { key: "design", label: "Design", color: "#06b6d4" },
  ];

  const filteredTechnologies =
    selectedCategory === "all"
      ? technologies
      : technologies.filter((tech) => tech.category === selectedCategory);

  return (
    <div className="w-full">
      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-3 mb-8">
        {categories.map((category) => (
          <motion.button
            key={category.key}
            onClick={() => setSelectedCategory(category.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
              selectedCategory === category.key
                ? "text-white shadow-lg"
                : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
            }`}
            style={{
              backgroundColor:
                selectedCategory === category.key ? category.color : undefined,
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            {category.label}
          </motion.button>
        ))}
      </div>

      {/* Conditional Rendering: Show 3D scene on desktop, 2D grid on mobile */}
      {isMobile ? (
        <MobileTechStack technologies={filteredTechnologies} />
      ) : (
        <>
          {/* 3D Canvas for large screens */}
          <div className="relative h-96 md:h-[500px] bg-gradient-to-br from-gray-900 to-black rounded-2xl overflow-hidden mb-6">
            <Canvas camera={{ position: [0, 0, 8], fov: 60 }}>
              <TechStack3DScene
                technologies={filteredTechnologies}
                hoveredTech={hoveredTech}
                setHoveredTech={setHoveredTech}
              />
              <OrbitControls
                enableZoom={false}
                enablePan={false}
                autoRotate
                autoRotateSpeed={0.5}
              />
            </Canvas>

              {/* Instruction hints */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-3 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: [0, 1, 1, 0], y: [10, 0, 0, -6] }}
                transition={{ duration: 4, delay: 0.5, times: [0, 0.15, 0.8, 1] }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white backdrop-blur-md border border-white/20"
                style={{ background: "rgba(16,185,129,0.25)" }}
              >
                <span className="text-base">🖱️</span> Drag to rotate
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: [0, 1, 1, 0], y: [10, 0, 0, -6] }}
                transition={{ duration: 4, delay: 0.8, times: [0, 0.15, 0.8, 1] }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-white backdrop-blur-md border border-white/20"
                style={{ background: "rgba(99,102,241,0.25)" }}
              >
                <span className="text-base">👆</span> Hover for details
              </motion.div>
            </div>

            {/* Hover detail card */}
            {hoveredTech && (
              <motion.div
                key={hoveredTech.name}
                initial={{ opacity: 0, scale: 0.9, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.15 }}
                className="absolute top-4 right-4 min-w-[160px] rounded-xl px-4 py-3 backdrop-blur-md border border-white/10 text-white shadow-xl"
                style={{ background: `${hoveredTech.color}30`, borderColor: `${hoveredTech.color}50` }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: hoveredTech.color }} />
                  <span className="font-bold text-sm">{hoveredTech.name}</span>
                </div>
                <div className="text-xs text-white/70 mb-1.5 capitalize">{hoveredTech.category}</div>
                <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ backgroundColor: hoveredTech.color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${hoveredTech.level}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>
                <div className="text-xs text-white/60 mt-1 text-right">{hoveredTech.level}%</div>
              </motion.div>
            )}
          </div>
        </>
      )}

    </div>
  );
};

export default TechStack3D;
